import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Eye,
  X,
  Users,
  Video,
  CalendarClock,
  RefreshCw,
  AlertTriangle,
  Activity,
  Calendar,
  Mail
} from 'lucide-react';
import { toast } from "sonner";
import { AddInterviewDialog } from '@/components/hiring/AddInterviewDialog';
import InterviewViewDialog from '@/components/hiring/InterviewViewDialog';
import InterviewTimelineDialog from '@/components/hiring/InterviewTimelineDialog';
import { interviewService } from '@/services/interview.service';
import { format } from 'date-fns';
import { useAuthStore } from '@/stores/authStore';
import { useSharedData } from '@/hooks/useSharedData';
import { TimeSelect } from '@/components/ui/time-picker';
import { InterviewStatus } from '@/types/models';

interface InterviewsListProps {
  limit?: number;
  showFilters?: boolean;
}

const getCandidateName = (candidate: any) => {
  if (!candidate) return 'Candidate Deleted';
  if (typeof candidate === 'object') {
    const firstName = candidate.firstName || '';
    const lastName = candidate.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Unknown Candidate';
  }
  return candidate;
};

const getCandidateEmail = (candidate: any) => {
  if (!candidate) return '';
  if (typeof candidate === 'object') {
    return candidate.email || '';
  }
  return '';
};




const getInterviewerNames = (interviewers: any[]) => {
  if (!Array.isArray(interviewers)) return 'No Interviewers';
  if (interviewers.length === 0) return 'No Interviewers';

  return interviewers.map((interviewer: any) => {
    if (!interviewer) return 'Unknown Interviewer';
    if (typeof interviewer === 'string') return interviewer;
    if (interviewer && typeof interviewer === 'object') {
      return interviewer.user ? `${interviewer.user.firstName} ${interviewer.user.lastName}` : interviewer.name || 'Unknown Interviewer';
    }
    return 'Unknown Interviewer';
  }).join(', ');
};

function getStatusString(status: any) {
  if (!status) return '';
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status !== null && 'name' in status) return status.name;
  return String(status);
}

// Helper function to check if status is cancelled
const isCancelledStatus = (status: any) => {
  const statusStr = getStatusString(status);
  return statusStr.toLowerCase().includes('cancel') ||
    statusStr.toLowerCase().includes('terminated') ||
    statusStr.toLowerCase().includes('cancelled');
};

// Helper to format time strings (HH:mm:ss or ISO to 12h format)
const formatTimeStr = (str: string) => {
  if (!str) return '';
  // Check if it's ISO format
  if (str.includes('T') || str.includes('Z')) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    }
  }
  // Check if it's HH:mm:ss or HH:mm
  const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeMatch) {
    let [_, hours, minutes] = timeMatch;
    let h = parseInt(hours);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    return `${h}:${m} ${ampm}`;
  }
  return str;
};

const InterviewsList: React.FC<InterviewsListProps> = ({ limit, showFilters = true }) => {
  const { hasSubModulePermission } = useAuthStore();
  const { interviews: sharedInterviews, refreshInterviews } = useSharedData();

  const interviewStatuses = [
    { id: InterviewStatus.SCHEDULED, name: 'Scheduled' },
    { id: InterviewStatus.CONFIRMED, name: 'Confirmed' },
    { id: InterviewStatus.COMPLETED, name: 'Completed' },
    { id: InterviewStatus.CANCELLED, name: 'Cancelled' },
    { id: InterviewStatus.RESCHEDULED, name: 'Rescheduled' },
  ];

  // Function to get department name by ID

  // Function to fetch interviews with optimized loading
  const fetchInterviews = React.useCallback(async (showToast = false, _forceRefresh = false) => {
    setIsLoading(true);
    setRefreshError(null);

    try {


      const res: any = await interviewService.getInterviews();



      let apiData: any[] = [];

      // Handle the response format from the service
      if (res && typeof res === 'object' && Array.isArray(res.data)) {

        apiData = res.data;
      } else if (Array.isArray(res)) {

        apiData = res;
      } else if (res && typeof res === 'object' && res.data && Array.isArray(res.data.data)) {

        apiData = res.data.data;
      } else {
        console.warn('Unexpected response format:', res);
        apiData = [];
      }



      // Always update the state with new data
      setInterviews(apiData);
      setLastRefreshTime(new Date());

      if (showToast) {
        toast.success(`Data updated! Found ${apiData.length} interviews.`);
      }

      return apiData;
    } catch (err) {
      console.error('=== DEBUG: Error fetching interviews ===');
      console.error('Error:', err);
      console.error('Error type:', typeof err);
      console.error('Error message:', err instanceof Error ? err.message : 'Unknown error');

      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch interviews';
      setRefreshError(errorMessage);
      toast.error(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, []); // Remove all dependencies to prevent unnecessary re-renders

  // Manual refresh function
  const handleManualRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    setRefreshError(null);

    try {
      // First refresh shared data
      await refreshInterviews();

      // Then fetch fresh data directly
      const freshData = await fetchInterviews(true, true);

      // Update local state immediately
      setInterviews(freshData);

      toast.success(`Refreshed! Found ${freshData.length} interviews.`);
    } catch (error) {
      console.error('Error during manual refresh:', error);
      toast.error('Failed to refresh interviews');
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshInterviews, fetchInterviews]);

  // Listen for candidate status changes that might create interviews
  React.useEffect(() => {
    const handleCandidateStatusChange = (event: CustomEvent) => {
      const { status, category } = event.detail;

      // If candidate status changes to interview, refresh interview list
      if (category === 'candidate' && status && status.toLowerCase().includes('interview')) {

        handleManualRefresh();
      }
    };

    window.addEventListener('candidate-status-updated', handleCandidateStatusChange as EventListener);

    return () => {
      window.removeEventListener('candidate-status-updated', handleCandidateStatusChange as EventListener);
    };
  }, [handleManualRefresh]);

  // Permission checks for interview actions
  const canViewInterviews = hasSubModulePermission('hiring', 'interview', 'view');
  const canCreateInterviews = hasSubModulePermission('hiring', 'interview', 'create');
  const canEditInterviews = hasSubModulePermission('hiring', 'interview', 'edit');
  const canDeleteInterviews = hasSubModulePermission('hiring', 'interview', 'delete');

  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [dateRangeFilter, setDateRangeFilter] = React.useState('all');
  const [sortConfig, _setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'interviewDate', direction: 'desc' });
  const [selectedTimelineEvent, setSelectedTimelineEvent] = React.useState<{ event: any; interview: any } | null>(null);
  const [selectedInterview, setSelectedInterview] = React.useState<any>(null);
  const [isInterviewUpdating, setIsInterviewUpdating] = React.useState(false);
  const [interviews, setInterviews] = React.useState<any[]>([]);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = React.useState(false);
  const [interviewToReschedule, setInterviewToReschedule] = React.useState<any | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [_refreshKey] = React.useState(0);
  const [_lastRefreshTime, setLastRefreshTime] = React.useState<Date | null>(null);
  const [_refreshError, setRefreshError] = React.useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [interviewToEdit, setInterviewToEdit] = React.useState<any | null>(null);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Add state for reschedule dialog
  const [isRescheduleStatusDialogOpen, setIsRescheduleStatusDialogOpen] = React.useState(false);
  const [rescheduleStatusData, setRescheduleStatusData] = React.useState({
    interviewId: '',
    newStatus: '',
    statusName: '',
    newDate: '',
    newTime: ''
  });

  // Confirm dialog state and helper functions
  const [confirmDialog, setConfirmDialog] = React.useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    cancelText: string;
    type: 'danger' | 'warning' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'info'
  });

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info';
    }
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      type: options?.type || 'info'
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    closeConfirmDialog();
  };



  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDateRangeFilter('all');
    toast.success('Filters cleared');
  };

  const getStatusColor = (status: string) => {
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case InterviewStatus.SCHEDULED:
        return '#3B82F6'; // blue
      case InterviewStatus.CONFIRMED:
        return '#8B5CF6'; // purple
      case InterviewStatus.COMPLETED:
        return '#10B981'; // green
      case InterviewStatus.CANCELLED:
        return '#EF4444'; // red
      case InterviewStatus.RESCHEDULED:
        return '#F59E0B'; // yellow
      default:
        // Handle common variations
        if (statusUpper.includes('OFFER')) return '#F59E0B';
        if (statusUpper.includes('HIRE')) return '#059669';
        return '#6B7280'; // gray
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    const statusStr = getStatusString(status);
    const color = getStatusColor(statusStr);

    return {
      backgroundColor: color + '10',
      color: color,
      borderColor: color + '40'
    };
  };

  const getStatusDisplayName = (status: string) => {
    const statusStr = getStatusString(status);
    return statusStr.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!canEditInterviews) {
      toast.error("You don't have permission to edit interviews");
      return;
    }

    if (!selectedInterview) return;

    if (!selectedInterview.candidate) {
      toast.error("Cannot update status for interview with deleted candidate");
      return;
    }

    // Validate status transition
    const validationError = validateInterviewStatusTransition(selectedInterview, newStatus);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const statusObj = interviewStatuses.find(s => s.id === newStatus);
    const statusName = statusObj ? statusObj.name : newStatus;

    // Check if this is a cancelled status
    const isCancelledStatus = statusName.toLowerCase().includes('cancelled') ||
      newStatus.toLowerCase().includes('cancelled') ||
      statusName.toLowerCase().includes('cancel') ||
      newStatus.toLowerCase().includes('cancel');

    if (isCancelledStatus) {
      // Show special confirmation for cancelled status
      showConfirmDialog(
        'Terminate Interview',
        `Are you sure you want to terminate the interview for ${getCandidateName(selectedInterview.candidate)}?\n\nThis action will permanently cancel the interview and cannot be undone.`,
        () => performStatusUpdate(newStatus, statusName),
        {
          confirmText: 'Terminate Interview',
          cancelText: 'Cancel',
          type: 'danger'
        }
      );
    } else {
      // Check if this is a reschedule status (check by name or ID)
      const isRescheduleStatus = statusName.toLowerCase().includes('reschedule') ||
        newStatus.toLowerCase().includes('reschedule') ||
        statusName.toLowerCase().includes('rescheduled') ||
        newStatus.toLowerCase().includes('rescheduled');

      if (isRescheduleStatus) {
        // Open reschedule dialog for date/time selection
        setRescheduleStatusData({
          interviewId: selectedInterview.id || selectedInterview._id,
          newStatus: newStatus,
          statusName: statusName,
          newDate: '',
          newTime: ''
        });
        setIsRescheduleStatusDialogOpen(true);
      } else {
        // Regular status update
        showConfirmDialog(
          'Update Interview Status',
          `Are you sure you want to update the interview status to "${getStatusDisplayName(newStatus)}" for ${getCandidateName(selectedInterview.candidate)}?`,
          () => performStatusUpdate(newStatus, newStatus),
          {
            confirmText: 'Update Status',
            cancelText: 'Cancel',
            type: 'warning'
          }
        );
      }
    }
  };

  // Helper function to validate interview status transitions
  const validateInterviewStatusTransition = (interview: any, newStatus: string) => {
    const currentStatus = getStatusString(interview.status).toLowerCase();
    const newStatusStr = newStatus.toLowerCase();

    // Rule 1: Once "Scheduled", cannot go back to "Scheduled" - schedule is permanently disabled
    if (newStatusStr === 'scheduled' && currentStatus !== 'scheduled') {
      return 'Cannot schedule interview again. Once an interview is scheduled, it cannot be re-scheduled.';
    }

    // Rule 2: Once "Confirmed", cannot be confirmed again
    if (newStatusStr === 'confirmed' && currentStatus === 'confirmed') {
      return 'Interview is already confirmed. Cannot confirm the same interview twice.';
    }

    // Rule 3: Once "Confirmed", cannot go back to "Scheduled"
    if (newStatusStr === 'scheduled' && currentStatus === 'confirmed') {
      return 'Cannot return to "Scheduled" status once interview is confirmed.';
    }

    // Rule 4: Reschedule is only allowed before confirmation
    if (newStatusStr === 'rescheduled' && currentStatus === 'confirmed') {
      return 'Cannot reschedule interview after it has been confirmed.';
    }

    // Rule 5: Once "Completed", all status changes are disabled
    if (currentStatus === 'completed') {
      return 'Cannot change status for completed interviews. Once an interview is completed, no further status changes are allowed.';
    }

    // Rule 6: Cannot move to "Completed" from "Scheduled" (must be confirmed first)
    if (newStatusStr === 'completed' && currentStatus === 'scheduled') {
      return 'Interview must be confirmed before it can be marked as completed.';
    }

    // Rule 7: Cannot move to "Offered" or "Hired" from "Scheduled" (must be completed first)
    if ((newStatusStr === 'offered' || newStatusStr === 'hired') && currentStatus === 'scheduled') {
      return 'Interview must be completed before moving to offered or hired status.';
    }

    // Rule 8: Cannot move to "Hired" from "Confirmed" (must be completed first)
    if (newStatusStr === 'hired' && currentStatus === 'confirmed') {
      return 'Interview must be completed before moving to hired status.';
    }

    // Rule 9: Cannot move to "Offered" from "Confirmed" (must be completed first)
    if (newStatusStr === 'offered' && currentStatus === 'confirmed') {
      return 'Interview must be completed before moving to offered status.';
    }

    // Rule 10: Once "Offered" or "Hired", no further status changes allowed
    if (currentStatus === 'offered' || currentStatus === 'hired') {
      return 'Cannot change status for offered or hired interviews.';
    }

    return null; // No validation error
  };

  const handleRescheduleStatusConfirm = async () => {
    if (!rescheduleStatusData.newDate || !rescheduleStatusData.newTime) {
      toast.error('Please select both date and time');
      return;
    }

    setIsInterviewUpdating(true);

    // Store the original interview for rollback in case of error
    const originalInterview = selectedInterview;

    // Close dialog immediately when starting the reschedule process
    setIsRescheduleStatusDialogOpen(false);
    const interviewId = rescheduleStatusData.interviewId;

    try {
      // Optimistically update the UI immediately for instant feedback
      const optimisticInterview = {
        ...selectedInterview,
        interviewDate: rescheduleStatusData.newDate,
        interviewTime: rescheduleStatusData.newTime,
        status: rescheduleStatusData.statusName, // Use statusName instead of newStatus (ID)
        // Add a timeline entry for the reschedule
        timeline: [
          ...(selectedInterview.timeline || []),
          {
            _id: `temp_${Date.now()}`,
            type: 'status_change',
            status: 'rescheduled',
            description: `Interview rescheduled to ${rescheduleStatusData.newDate} at ${rescheduleStatusData.newTime}`,
            date: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            createdBy: {
              firstName: 'You',
              lastName: ''
            }
          }
        ]
      };

      // Update local state immediately
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || i.id) === interviewId ? optimisticInterview : i)
      );
      setSelectedInterview(optimisticInterview);

      // Show immediate success feedback
      toast.success(`Interview rescheduled to ${rescheduleStatusData.newDate} at ${rescheduleStatusData.newTime} for ${getCandidateName(selectedInterview.candidate)}`);

      // Reset reschedule data
      setRescheduleStatusData({
        interviewId: '',
        newStatus: '',
        statusName: '',
        newDate: '',
        newTime: ''
      });

      // Make the API calls in the background
      // First update the interview date and time
      const updateData = {
        interviewDate: rescheduleStatusData.newDate,
        interviewTime: rescheduleStatusData.newTime
      };

      await interviewService.updateInterview(interviewId, updateData);

      // Then update the status to rescheduled
      await interviewService.updateInterviewStatus(interviewId, rescheduleStatusData.newStatus);

      // Fetch the updated interview from the backend to ensure consistency
      const refreshedInterview = await interviewService.getInterviewById(interviewId);

      if (!refreshedInterview) {
        console.warn('Failed to fetch updated interview from backend, keeping optimistic update');
        return;
      }

      // Update local state with the server response for full accuracy
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || (i as any).id) === interviewId ? refreshedInterview : i)
      );
      setSelectedInterview(refreshedInterview);

      // Refresh shared data to ensure consistency
      await refreshInterviews();

    } catch (error) {
      console.error('Error rescheduling interview:', error);

      // Revert the optimistic update on error
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || i.id) === interviewId ? originalInterview : i)
      );
      setSelectedInterview(originalInterview);

      toast.error('Failed to reschedule interview');
    } finally {
      setIsInterviewUpdating(false);
    }
  };

  const performStatusUpdate = async (newStatus: string, statusName: string) => {
    setIsInterviewUpdating(true);
    const interviewId = selectedInterview.id || selectedInterview._id;



    // Store the original interview for rollback in case of error
    const originalInterview = selectedInterview;

    try {
      // Optimistically update the UI immediately for instant feedback
      const optimisticInterview = {
        ...selectedInterview,
        status: statusName, // Use statusName instead of newStatus (ID)
        // Add a timeline entry for the status change
        timeline: [
          ...(selectedInterview.timeline || []),
          {
            _id: `temp_${Date.now()}`,
            type: 'status_change',
            status: statusName,
            description: `Status updated to ${statusName}`,
            date: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            createdBy: {
              firstName: 'You',
              lastName: ''
            }
          }
        ]
      };

      // Update local state immediately
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || (i as any).id) === interviewId ? optimisticInterview : i)
      );
      setSelectedInterview(optimisticInterview);

      // Show immediate success feedback
      toast.success(`Status updated to ${statusName} for ${getCandidateName(selectedInterview.candidate)}`);



      // Make the API call in the background
      await interviewService.updateInterviewStatus(interviewId, newStatus);

      // Fetch the updated interview from the backend to ensure consistency
      const refreshedInterview = await interviewService.getInterviewById(interviewId);

      if (!refreshedInterview) {
        console.warn('Failed to fetch updated interview from backend, keeping optimistic update');
        return;
      }

      // Update local state with the server response for full accuracy
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || (i as any).id) === (refreshedInterview._id || (refreshedInterview as any).id) ? refreshedInterview : i)
      );
      setSelectedInterview(refreshedInterview);

      // Refresh shared data to ensure consistency
      await refreshInterviews();

      // Close dialog if status is cancelled
      if (newStatus.toUpperCase() === InterviewStatus.CANCELLED || statusName.toUpperCase() === InterviewStatus.CANCELLED) {
        setSelectedInterview(null);
      }
    } catch (error) {
      console.error('Error updating interview status:', error);

      // Revert the optimistic update on error
      setInterviews((prev: any[]) =>
        prev
          .filter(i => !!i)
          .map(i => (i._id || (i as any).id) === interviewId ? originalInterview : i)
      );
      setSelectedInterview(originalInterview);

      toast.error('Failed to update interview status');
    } finally {
      setIsInterviewUpdating(false);
    }
  };

  const handleSaveInterview = async () => { };

  const filteredInterviews = React.useMemo(() => {
    return interviews.filter(interview => {
      if (!interview) return false;

      const candidateName = getCandidateName(interview.candidate);
      const position = interview.position || '';
      const searchMatch =
        candidateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        position.toLowerCase().includes(searchQuery.toLowerCase());

      const statusMatch = statusFilter === 'all' || getStatusString(interview.status).toUpperCase() === statusFilter.toUpperCase();
      const typeMatch = typeFilter === 'all' || interview.interviewType === typeFilter;

      // Remove the interviewDate check to show all interviews
      // if (!interview.interviewDate) return false;

      const interviewDate = interview.interviewDate ? new Date(interview.interviewDate) : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let dateMatch = true; // Default to true for interviews without dates

      if (interviewDate && dateRangeFilter !== 'all') {
        const interviewDateOnly = new Date(interviewDate);
        interviewDateOnly.setHours(0, 0, 0, 0);

        // Calculate date ranges
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);

        const nextMonth = new Date(today);
        nextMonth.setDate(nextMonth.getDate() + 30);

        dateMatch =
          (dateRangeFilter === 'today' && interviewDateOnly.getTime() === today.getTime()) ||
          (dateRangeFilter === 'tomorrow' && interviewDateOnly.getTime() === tomorrow.getTime()) ||
          (dateRangeFilter === 'next7days' && interviewDateOnly.getTime() >= today.getTime() && interviewDateOnly.getTime() < nextWeek.getTime()) ||
          (dateRangeFilter === 'next30days' && interviewDateOnly.getTime() >= today.getTime() && interviewDateOnly.getTime() < nextMonth.getTime());
      }

      return searchMatch && statusMatch && typeMatch && dateMatch;
    });
  }, [interviews, searchQuery, statusFilter, typeFilter, dateRangeFilter]);

  const sortedInterviews = React.useMemo(() => {
    return [...filteredInterviews].sort((a, b) => {
      if (!a || !b) return 0;

      // Get status strings for comparison
      const aStatusStr = getStatusString(a.status);
      const bStatusStr = getStatusString(b.status);

      // Priority order for status: scheduled > confirmed > rescheduled > completed > offered > hired > cancelled
      const statusPriority = {
        'scheduled': 1,
        'confirmed': 2,
        'rescheduled': 3,
        'completed': 4,
        'offered': 5,
        'hired': 6,
        'cancelled': 7
      };

      // Convert status to lowercase for case-insensitive comparison
      const aStatus = aStatusStr.toLowerCase();
      const bStatus = bStatusStr.toLowerCase();

      // Function to get priority for a status
      const getStatusPriority = (status: string) => {
        // Check if it's a main status first
        if (statusPriority[status as keyof typeof statusPriority]) {
          return statusPriority[status as keyof typeof statusPriority];
        }

        // Unknown statuses get lowest priority
        return 8;
      };

      const aPriority = getStatusPriority(aStatus);
      const bPriority = getStatusPriority(bStatus);

      // First sort by status priority (scheduled first, cancelled last)
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // If same status, sort by interview date (earliest first for active interviews)
      if (a.interviewDate && b.interviewDate) {
        const aDate = new Date(a.interviewDate).getTime();
        const bDate = new Date(b.interviewDate).getTime();

        // For completed interviews, show most recent first
        if (aStatus === 'completed' && bStatus === 'completed') {
          return bDate - aDate; // Most recent completed interviews first
        }

        // For active interviews, show earliest first
        return aDate - bDate;
      }

      // If no date, sort by candidate name
      if (sortConfig.key === 'candidateName') {
        return sortConfig.direction === 'asc'
          ? getCandidateName(a.candidate).localeCompare(getCandidateName(b.candidate))
          : getCandidateName(b.candidate).localeCompare(getCandidateName(a.candidate));
      }

      return 0;
    });
  }, [filteredInterviews, sortConfig]);

  const displayedInterviews = limit ? sortedInterviews.slice(0, limit) : sortedInterviews;

  const handleTimelineClick = (event: any, interview: any) => {
    setSelectedTimelineEvent({ event, interview });
  };

  const handleViewInterview = (interview: any) => {
    if (!canViewInterviews) {
      toast.error("You don't have permission to view interviews");
      return;
    }
    setSelectedInterview(interview);
  };

  const handleRescheduleInterview = (interview: any) => {
    if (!canEditInterviews) {
      toast.error("You don't have permission to edit interviews");
      return;
    }

    if (!interview.candidate) {
      toast.error("Cannot reschedule interview with deleted candidate");
      return;
    }

    setInterviewToReschedule(interview);
    setIsRescheduleDialogOpen(true);
  };

  const handleSendReminder = (interview: any) => {
    if (!canEditInterviews) {
      toast.error("You don't have permission to edit interviews");
      return;
    }

    if (!interview.candidate) {
      toast.error("Cannot send reminder for interview with deleted candidate");
      return;
    }

    toast.success(`Reminder sent to ${getCandidateName(interview.candidate)}`);
  };

  // Function to delete an interview
  const handleDeleteInterview = async (interview: any) => {
    if (!canDeleteInterviews) {
      toast.error("You don't have permission to delete interviews");
      return;
    }

    const candidateName = getCandidateName(interview.candidate);

    showConfirmDialog(
      'Delete Interview',
      `Are you sure you want to delete the interview for ${candidateName}?\n\nThis action cannot be undone.`,
      async () => {
        try {
          const interviewId = interview.id || interview._id;
          await interviewService.deleteInterview(interviewId);

          // Close the view dialog if it's open for the deleted interview
          if (selectedInterview && (selectedInterview.id || selectedInterview._id) === interviewId) {
            setSelectedInterview(null);
          }

          // Update local state by removing the deleted interview
          setInterviews(prev => prev.filter(i => (i._id || (i as any).id) !== interviewId));

          // Refresh shared data to ensure consistency
          await refreshInterviews();

          // Trigger the callback to refresh candidate dropdowns
          handleInterviewDeleted();

          toast.success(`Interview for ${candidateName} deleted successfully.`);
        } catch (error) {
          console.error('Error deleting interview:', error);
          toast.error('Failed to delete interview');
        }
      },
      {
        confirmText: 'Delete Interview',
        cancelText: 'Cancel',
        type: 'danger'
      }
    );
  };

  const handleAddInterview = async () => {
    try {
      // Refresh data immediately after adding
      await handleManualRefresh();
      toast.success('Interview added successfully');
    } catch (err) {
      console.error('Error adding interview:', err);
      toast.error('Failed to add interview');
    }
  };

  const handleUpdateInterview = async (updatedInterview: any) => {
    // Store original interviews for rollback in case of error
    const originalInterviews = [...interviews];
    const originalSelectedInterview = selectedInterview;

    try {
      // Immediately update local state for instant UI feedback
      setInterviews(prev => prev.map(i => (i._id || (i as any).id) === (updatedInterview._id || (updatedInterview as any).id) ? updatedInterview : i));
      if (selectedInterview && (selectedInterview._id || (selectedInterview as any).id) === (updatedInterview._id || (updatedInterview as any).id)) {
        setSelectedInterview(updatedInterview);
      }

      let updated: any;

      if (isRescheduleDialogOpen && interviewToReschedule) {
        // This is a reschedule operation - update date/time and set status to rescheduled
        const interviewId = interviewToReschedule.id || interviewToReschedule._id;

        // Optimistically update the interview with rescheduled status and timeline entry
        const optimisticRescheduledInterview = {
          ...updatedInterview,
          status: 'rescheduled',
          timeline: [
            ...(updatedInterview.timeline || []),
            {
              _id: `temp_${Date.now()}`,
              type: 'status_change',
              status: 'rescheduled',
              description: `Interview rescheduled to ${updatedInterview.interviewDate} at ${updatedInterview.interviewTime}`,
              date: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              createdBy: {
                firstName: 'You',
                lastName: ''
              }
            }
          ]
        };

        // Update local state immediately with optimistic data
        setInterviews(prev => prev.map(i => (i._id || (i as any).id) === interviewId ? optimisticRescheduledInterview : i));
        if (selectedInterview && (selectedInterview._id || (selectedInterview as any).id) === interviewId) {
          setSelectedInterview(optimisticRescheduledInterview);
        }

        // Show immediate success feedback
        toast.success(`Interview rescheduled for ${getCandidateName(updatedInterview.candidate)}`);

        // Make API calls in the background
        // 1. Update the interview's date/time fields
        const reschedulePayload = {
          interviewDate: updatedInterview.interviewDate,
          interviewTime: updatedInterview.interviewTime,
        };
        updated = await interviewService.updateInterview(interviewId, reschedulePayload);

        // 2. Update the status to 'rescheduled' (this will also add a timeline entry)
        updated = await interviewService.updateInterviewStatus(interviewId, 'rescheduled');

        // Update local state with server response for consistency
        setInterviews(prev => prev.map(i => (i._id || (i as any).id) === (updated._id || (updated as any).id) ? updated : i));
        if (selectedInterview && (selectedInterview._id || (selectedInterview as any).id) === (updated._id || (updated as any).id)) {
          setSelectedInterview(updated);
        }

        // Refresh shared data to ensure consistency
        await refreshInterviews();

      } else if (isEditDialogOpen && interviewToEdit) {
        // This is an edit operation - create clean payload with only allowed fields and extract IDs
        const cleanPayload = {
          interviewDate: updatedInterview.interviewDate,
          interviewTime: updatedInterview.interviewTime,
          candidate: (updatedInterview.candidate && typeof updatedInterview.candidate === 'object') ? updatedInterview.candidate._id : updatedInterview.candidate,
          department: (updatedInterview.department && typeof updatedInterview.department === 'object') ? updatedInterview.department._id : updatedInterview.department,
          interviewers: Array.isArray(updatedInterview.interviewers)
            ? updatedInterview.interviewers.map((interviewer: any) =>
              typeof interviewer === 'object' ? interviewer._id : interviewer
            )
            : updatedInterview.interviewers,
          interviewType: updatedInterview.interviewType,
          location: updatedInterview.location,
          notes: updatedInterview.notes,
        };


        updated = await interviewService.updateInterview(interviewToEdit._id, cleanPayload);

        // Update local state with server response
        setInterviews(prev => prev.map(i => (i._id || (i as any).id) === (updated._id || (updated as any).id) ? updated : i));
        if (selectedInterview && (selectedInterview._id || (selectedInterview as any).id) === (updated._id || (updated as any).id)) {
          setSelectedInterview(updated);
        }

        // Refresh shared data to ensure consistency
        await refreshInterviews();

        toast.success(`Interview updated for ${getCandidateName(updated.candidate)}`);
      } else {
        // Fallback for other cases - create clean payload with ID extraction
        const cleanPayload = {
          interviewDate: updatedInterview.interviewDate,
          interviewTime: updatedInterview.interviewTime,
          candidate: (updatedInterview.candidate && typeof updatedInterview.candidate === 'object') ? updatedInterview.candidate._id : updatedInterview.candidate,
          department: (updatedInterview.department && typeof updatedInterview.department === 'object') ? updatedInterview.department._id : updatedInterview.department,
          interviewers: Array.isArray(updatedInterview.interviewers)
            ? updatedInterview.interviewers.map((interviewer: any) =>
              typeof interviewer === 'object' ? interviewer._id : interviewer
            )
            : updatedInterview.interviewers,
          interviewType: updatedInterview.interviewType,
          location: updatedInterview.location,
          notes: updatedInterview.notes,
        };

        updated = await interviewService.updateInterview(updatedInterview._id, cleanPayload);

        // Update local state with server response
        setInterviews(prev => prev.map(i => (i._id || (i as any).id) === (updated._id || (updated as any).id) ? updated : i));
        if (selectedInterview && (selectedInterview._id || (selectedInterview as any).id) === (updated._id || (updated as any).id)) {
          setSelectedInterview(updated);
        }

        // Refresh shared data to ensure consistency
        await refreshInterviews();

        toast.success(`Interview updated for ${getCandidateName(updated.candidate)}`);
      }

      // Close dialogs and reset state
      setIsEditDialogOpen(false);
      setInterviewToEdit(null);
      setIsRescheduleDialogOpen(false);
      setInterviewToReschedule(null);
    } catch (error) {
      console.error('Error updating interview:', error);

      // Revert optimistic updates on error
      setInterviews(originalInterviews);
      setSelectedInterview(originalSelectedInterview);

      toast.error('Failed to update interview');
    }
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy');
    } catch {
      return dateStr;
    }
  };

  // Function to check if interview is today or overdue
  const getInterviewDateStatus = (interviewDate: string) => {
    if (!interviewDate) return { isToday: false, isOverdue: false, message: '' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const interviewDateObj = new Date(interviewDate);
    interviewDateObj.setHours(0, 0, 0, 0);

    const isToday = interviewDateObj.getTime() === today.getTime();
    const isOverdue = interviewDateObj.getTime() < today.getTime();

    let message = '';
    if (isToday) {
      message = 'Today';
    } else if (isOverdue) {
      const daysDiff = Math.floor((today.getTime() - interviewDateObj.getTime()) / (1000 * 60 * 60 * 24));
      message = `${daysDiff} day${daysDiff > 1 ? 's' : ''} overdue`;
    }

    return { isToday, isOverdue, message };
  };

  const handleEditInterview = (interview: any) => {
    if (!canEditInterviews) {
      toast.error("You don't have permission to edit interviews");
      return;
    }

    if (!interview.candidate) {
      toast.error("Cannot edit interview with deleted candidate");
      return;
    }

    if (interview.candidate && typeof interview.candidate === 'object') {

    }

    if (interview.candidate && typeof interview.candidate === 'object') {

    }

    if (interview.department && typeof interview.department === 'object') {

    }

    if (interview.interviewers && Array.isArray(interview.interviewers)) {

      interview.interviewers.forEach((interviewer: any, _index: number) => {

        if (typeof interviewer === 'object') {

        }
      });
    }
    setInterviewToEdit(interview);
    setIsEditDialogOpen(true);
  };

  // Callback to refresh candidate dropdown when interview is deleted
  const handleInterviewDeleted = () => {
    // This will trigger the AddInterviewDialog to refresh its candidate list
    // The callback is passed to all AddInterviewDialog components
  };

  // Use shared data if available, otherwise fetch locally
  React.useEffect(() => {
    // Use shared interviews data if available
    if (sharedInterviews.length > 0) {
      setInterviews(sharedInterviews);
      setIsInitialLoad(false);
    } else {
      // Only fetch if we don't have shared data
      const loadInitialData = async () => {
        try {
          await fetchInterviews(false, true);
        } catch (error) {
          console.error('Error during initial data fetch:', error);
        }
      };

      loadInitialData();
    }
  }, [sharedInterviews, fetchInterviews]); // Run when shared data changes

  // Initialize data when component mounts
  React.useEffect(() => {
    const initializeData = async () => {
      try {
        // Only fetch fresh data on mount, no periodic refresh
        await fetchInterviews(false, true);
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    };

    initializeData();
  }, []); // Run only once on mount

  // Permission guard - hide entire component if no view permission
  if (!canViewInterviews) {
    return (
      <div className="space-y-6 p-6 pb-16">
        <div className="text-center py-12">
          <p className="text-gray-500">You don't have permission to view interviews.</p>
        </div>
      </div>
    );
  }

  function getTimelineStatusDisplay(status: any) {
    const statusStr = getStatusString(status);

    // Handle "created" events specifically - show as "Scheduled"
    if (statusStr && statusStr.toLowerCase().includes('created')) {
      return 'Scheduled';
    }

    return getStatusDisplayName(statusStr) || 'Event';
  }
  function getTimelineStatusColor(status: any) {
    const statusStr = getStatusString(status);
    return getStatusColor(statusStr);
  }

  return (
    <div className="space-y-6 p-6 pb-16">
      {showFilters && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-bold tracking-tight">Interviews</h2>
            <div className="flex items-center gap-2">
              {canCreateInterviews && (
                <AddInterviewDialog
                  open={false}
                  onOpenChange={() => { }}
                  onAddInterview={handleAddInterview}
                />
              )}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by candidate or position..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1 h-6 w-6"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <Activity className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {interviewStatuses.map((stat) => (
                    <SelectItem
                      key={stat.id}
                      value={stat.id}
                      disabled={statusFilter === stat.id}
                    >
                      {stat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Interview Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="tomorrow">Tomorrow</SelectItem>
                  <SelectItem value="next7days">Next 7 days</SelectItem>
                  <SelectItem value="next30days">Next 30 days</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                    <Video className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Interview Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="behavioural">Behavioural</SelectItem>
                    <SelectItem value="portfolio_review">Portfolio Review</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                  onClick={clearFilters}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {isInitialLoad || isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Interviews...</h2>
            <p className="text-gray-600">Please wait while we fetch your interview data.</p>
          </div>
        </div>

      ) : displayedInterviews.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {interviews.length === 0
              ? "No interviews found"
              : `No interviews match your current filters (${interviews.length} total interviews available)`
            }
          </p>
          {interviews.length === 0 && (
            <Button
              variant="outline"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="mt-4"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          )}
        </div>
      ) : (
        <>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedInterviews.map((interview) => {
              try {
                return (
                  <Card
                    key={interview._id || interview.id}
                    className={`hover:shadow-md transition-shadow h-[360px] sm:h-[380px] flex flex-col relative border-l-4 ${isCancelledStatus(interview.status) ? 'opacity-75 bg-gray-50' : ''} ${!interview.candidate ? 'border-red-200 bg-red-50' : ''}`}
                    style={{
                      borderLeftColor: !interview.candidate ? '#EF4444' : getStatusColor(getStatusString(interview.status))
                    }}
                  >
                    <CardHeader className="pb-3 flex-shrink-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg leading-tight mb-1">
                            {getCandidateName(interview.candidate)}
                            {!interview.candidate && (
                              <Badge variant="destructive" className="ml-2 text-xs">
                                Candidate Deleted
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-600">
                            {interview.jobPostingTitle
                              ? interview.jobPostingTitle
                              : (interview.position
                                ? (typeof interview.position === 'object'
                                  ? interview.position.title
                                  : interview.position)
                                : (interview.candidate && typeof interview.candidate === 'object' && interview.candidate.position
                                  ? (typeof interview.candidate.position === 'object'
                                    ? interview.candidate.position.title
                                    : interview.candidate.position)
                                  : 'Position not specified'))}
                          </CardDescription>
                        </div>
                        <Badge
                          className="border-current/20 text-xs font-medium"
                          style={getStatusBadgeStyle(interview.status)}
                        >
                          {getStatusDisplayName(interview.status)}
                          {getStatusString(interview.status).toLowerCase() === 'offered' && (
                            <span className="ml-1 text-[10px] opacity-75">→ Hired</span>
                          )}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="py-2 flex-1 flex flex-col overflow-hidden">
                      <div className="space-y-3 flex-shrink-0">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CalendarClock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="flex items-center gap-2">
                            {interview.interviewDate ? (
                              <span className="truncate">{formatDateDisplay(interview.interviewDate)} at {formatTimeStr(interview.interviewTime)}</span>
                            ) : (
                              <span className="truncate text-red-400 font-medium">Please set a date and time</span>
                            )}
                            {(() => {
                              const dateStatus = getInterviewDateStatus(interview.interviewDate);
                              // Don't show date status for completed or cancelled interviews
                              if (dateStatus.message && getStatusString(interview.status) !== 'completed' && !isCancelledStatus(interview.status)) {
                                return (
                                  <Badge
                                    variant="outline"
                                    className={`text-xs px-2 py-0.5 ${dateStatus.isToday
                                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                                      : 'bg-red-50 text-red-700 border-red-200'
                                      }`}
                                  >
                                    {dateStatus.message}
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Video className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{interview.interviewType}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{getInterviewerNames(interview.interviewers)}</span>
                        </div>
                        {getCandidateEmail(interview.candidate) && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{getCandidateEmail(interview.candidate)}</span>
                          </div>
                        )}
                      </div>

                      {interview.timeline && interview.timeline.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-gray-100 flex-1 flex flex-col justify-center min-h-0 overflow-auto">
                          {(() => {
                            // Create a map to track unique events by their content and timestamp
                            const uniqueEventsMap = new Map();
                            const timeline = [...interview.timeline];

                            timeline.forEach(ev => {
                              // Create a unique key based on event content and timestamp
                              const eventKey = `${ev.title || ev.status || 'Event'}_${ev.createdAt}_${ev.type || ev.subType}`;

                              if (!uniqueEventsMap.has(eventKey)) {
                                uniqueEventsMap.set(eventKey, ev);
                              }
                            });

                            // Convert map values to array and take first 4
                            const uniqueEvents = Array.from(uniqueEventsMap.values()).slice(0, 4);

                            return (
                              <>
                                {/* Desktop timeline (horizontal) */}
                                <div className="hidden sm:flex items-center justify-between relative">
                                  {uniqueEvents.map((event, index) => (
                                    <div key={`${event._id || index}_${event.createdAt}`} className="flex flex-col items-center relative flex-1">
                                      {index < uniqueEvents.length - 1 && (
                                        <div
                                          className="absolute top-1.5 left-1/2 h-0.5 bg-gray-200 z-0"
                                          style={{ width: 'calc(100% + 12px)', transform: 'translateX(6px)' }}
                                        />
                                      )}

                                      <div
                                        className="w-3 h-3 sm:w-4 sm:h-4 rounded-full z-10 relative cursor-pointer hover:scale-110 transition-transform"
                                        style={{ backgroundColor: '#3B82F6' }}
                                        onClick={() => handleTimelineClick(event, interview)}
                                        title={`${event.title || event.status} - ${formatDateDisplay(event.date) || new Date(event.createdAt).toLocaleDateString()}`}
                                      />

                                      <div className="mt-0.5 text-center">
                                        <p className="text-[10px] sm:text-xs font-medium text-gray-700 truncate max-w-16 sm:max-w-20">
                                          {event.type === 'status_change' || event.subType === 'status_change'
                                            ? getStatusDisplayName(event.metadata?.newStatus || event.status || event.title)
                                            : getTimelineStatusDisplay(event.status || event.title)
                                          }
                                        </p>
                                        <p className="text-[8px] sm:text-[10px] text-gray-500 mt-0.5">
                                          {formatDateDisplay(event.date) || new Date(event.createdAt).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Mobile timeline (vertical) */}
                                <div className="sm:hidden space-y-2 max-h-20 overflow-y-auto">
                                  {uniqueEvents.map((event, index) => (
                                    <div
                                      key={`${event._id || index}_${event.createdAt}`}
                                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                                      onClick={() => handleTimelineClick(event, interview)}
                                    >
                                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getTimelineStatusColor(event.status || event.title) }} />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[9px] font-medium text-gray-700 truncate">
                                          {getTimelineStatusDisplay(event.status || event.title)}
                                        </p>
                                        <p className="text-[8px] text-gray-500">
                                          {formatDateDisplay(event.date) || new Date(event.createdAt).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {interview.timeline.length > 4 && (
                                  <div className="text-center mt-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-[8px] sm:text-[10px] text-gray-500 hover:text-gray-700 h-4 sm:h-6 px-1 sm:px-2"
                                      onClick={() => {
                                        // Open timeline dialog with all events
                                        setSelectedTimelineEvent({
                                          event: null,
                                          interview: interview
                                        });
                                      }}
                                    >
                                      <span className="hidden sm:inline">View all {interview.timeline.length} events</span>
                                      <span className="sm:hidden">+{interview.timeline.length - 3}</span>
                                    </Button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="flex gap-2 pt-3 pb-3 px-4 flex-shrink-0 mt-auto border-t border-gray-50">
                      {canViewInterviews && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-8 text-xs px-2 py-1 rounded-full bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800"
                          onClick={() => handleViewInterview(interview)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      )}
                      {canEditInterviews && !isCancelledStatus(interview.status) && interview.candidate && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-8 text-xs px-2 py-1 rounded-full bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700 hover:text-orange-800"
                          onClick={() => handleEditInterview(interview)}
                        >
                          Edit
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              } catch (error) {
                console.error('Error rendering interview card:', error, interview);
                return (
                  <Card key={interview._id || interview.id} className="hover:shadow-md transition-shadow h-[360px] sm:h-[380px] flex flex-col relative border-l-4 border-red-200 bg-red-50">
                    <CardHeader className="pb-3 flex-shrink-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg leading-tight mb-1 text-red-700">
                            Error Loading Interview
                          </CardTitle>
                          <CardDescription className="text-sm text-red-600">
                            This interview could not be displayed due to data issues.
                          </CardDescription>
                        </div>
                        <Badge variant="destructive" className="text-xs">
                          Error
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="py-2 flex-1 flex flex-col justify-center">
                      <div className="text-center text-red-600">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                        <p className="text-sm">Interview data is corrupted or incomplete.</p>
                      </div>
                    </CardContent>
                    <CardFooter className="flex gap-2 pt-3 pb-3 px-4 flex-shrink-0 mt-auto border-t border-gray-50">
                      {canDeleteInterviews && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-8 text-xs px-2 py-1 rounded-full bg-red-50 hover:bg-red-100 border-red-200 text-red-700 hover:text-red-800"
                          onClick={() => handleDeleteInterview(interview)}
                        >
                          Delete
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              }
            })}
          </div>
        </>
      )}

      <InterviewTimelineDialog
        interview={selectedTimelineEvent?.interview || null}
        open={!!selectedTimelineEvent}
        onOpenChange={(open) => {
          if (!open) setSelectedTimelineEvent(null);
        }}
        initialEventId={selectedTimelineEvent?.event?._id || null}
      />

      <InterviewViewDialog
        interview={selectedInterview}
        open={!!selectedInterview}
        onOpenChange={(open) => {
          if (!open) {
            if (isInterviewUpdating) {
              if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                setSelectedInterview(null);
                setIsInterviewUpdating(false);
              }
            } else {
              setSelectedInterview(null);
            }
          }
        }}
        onUpdateStatus={handleUpdateStatus}
        onSendReminder={handleSendReminder}
        onReschedule={handleRescheduleInterview}
        pendingStatus={null}
        isDirty={false}
        onSave={handleSaveInterview}
        onEdit={handleEditInterview}
        onDelete={handleDeleteInterview}
        canDeleteInterviews={canDeleteInterviews}
        canEditInterviews={canEditInterviews}
      />

      <AddInterviewDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onAddInterview={handleUpdateInterview}
        interviewToEdit={interviewToEdit}
        isEditMode={true}
      />

      <AddInterviewDialog
        open={isRescheduleDialogOpen}
        onOpenChange={setIsRescheduleDialogOpen}
        onAddInterview={handleUpdateInterview}
        interviewToEdit={interviewToReschedule}
        isEditMode={true}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={closeConfirmDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.type === 'danger' && (
                <X className="h-5 w-5 text-red-500" />
              )}
              {confirmDialog.type === 'warning' && (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              )}
              {confirmDialog.type === 'info' && (
                <Activity className="h-5 w-5 text-blue-500" />
              )}
              {confirmDialog.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {confirmDialog.message}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeConfirmDialog}>
                {confirmDialog.cancelText}
              </Button>
              <Button variant={confirmDialog.type === 'danger' ? 'destructive' : 'default'} onClick={handleConfirmAction}>
                {confirmDialog.confirmText}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Status Dialog */}
      <Dialog open={isRescheduleStatusDialogOpen} onOpenChange={setIsRescheduleStatusDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-orange-500" />
              Reschedule Interview
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Please select the new date and time for the interview.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">New Date</label>
                <Input
                  type="date"
                  value={rescheduleStatusData.newDate}
                  onChange={(e) => setRescheduleStatusData(prev => ({ ...prev, newDate: e.target.value }))}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="text-sm font-medium">New Time</label>
                <TimeSelect
                  value={rescheduleStatusData.newTime}
                  onValueChange={(val: string) => setRescheduleStatusData(prev => ({ ...prev, newTime: val }))}
                  placeholder="Select Time"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsRescheduleStatusDialogOpen(false);
                  setRescheduleStatusData({
                    interviewId: '',
                    newStatus: '',
                    statusName: '',
                    newDate: '',
                    newTime: ''
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRescheduleStatusConfirm}
                disabled={!rescheduleStatusData.newDate || !rescheduleStatusData.newTime || isInterviewUpdating}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {isInterviewUpdating ? 'Rescheduling...' : 'Reschedule Interview'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default InterviewsList; 